import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { useUnreadNotificationsCount } from '@/store';
import { apiClient } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import SystemClock from './SystemClock';
import {
  HomeIcon,
  CubeIcon,
  SparklesIcon,
  RectangleGroupIcon,
  ShoppingCartIcon,
  UsersIcon,
  BeakerIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  BellIcon,
  XMarkIcon,
  BuildingStorefrontIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeSolidIcon,
  CubeIcon as CubeSolidIcon,
  SparklesIcon as SparklesSolidIcon,
  RectangleGroupIcon as RectangleGroupSolidIcon,
  ShoppingCartIcon as ShoppingCartSolidIcon,
  UsersIcon as UsersSolidIcon,
  BeakerIcon as BeakerSolidIcon,
  ChartBarIcon as ChartBarSolidIcon,
  Cog6ToothIcon as Cog6ToothSolidIcon,
  BellIcon as BellSolidIcon,
  BuildingStorefrontIcon as BuildingStorefrontSolidIcon,
  BoltIcon as BoltSolidIcon,
} from '@heroicons/react/24/solid';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  iconSolid: React.ComponentType<{ className?: string }>;
  badge?: number;
  description?: string;
  adminOnly?: boolean; // Флаг для пунктов, доступных только администраторам
}

interface SidebarProps {
  mobile?: boolean;
  onClose?: () => void;
}

const navigation: NavigationItem[] = [
  {
    name: 'Главная',
    href: '/',
    icon: HomeIcon,
    iconSolid: HomeSolidIcon,
    description: 'Обзор фермы и статистика',
  },
  {
    name: 'Стеллажи',
    href: '/stacks',
    icon: CubeIcon,
    iconSolid: CubeSolidIcon,
    description: 'Управление стеллажами и червями',
  },
  {
    name: 'Партии червей',
    href: '/batches',
    icon: RectangleGroupIcon,
    iconSolid: RectangleGroupSolidIcon,
    description: 'Жизненный цикл и партии',
  },
  {
    name: 'Склад',
    href: '/warehouse',
    icon: BuildingStorefrontIcon,
    iconSolid: BuildingStorefrontSolidIcon,
    description: 'Складские запасы червей',
  },
  {
    name: 'Продажи',
    href: '/sales',
    icon: ShoppingCartIcon,
    iconSolid: ShoppingCartSolidIcon,
    description: 'Заказы и выручка',
  },
  {
    name: 'Аналитика',
    href: '/analytics',
    icon: ChartBarIcon,
    iconSolid: ChartBarSolidIcon,
    description: 'Отчеты и графики',
  },
  {
    name: 'Управление реле',
    href: '/relays',
    icon: BoltIcon,
    iconSolid: BoltSolidIcon,
    description: 'IoT управление оборудованием',
  },
  {
    name: 'Уведомления',
    href: '/notifications',
    icon: BellIcon,
    iconSolid: BellSolidIcon,
    description: 'Центр уведомлений',
  },
  {
    name: 'Пользователи',
    href: '/users',
    icon: UsersIcon,
    iconSolid: UsersSolidIcon,
    description: 'Управление пользователями и группами',
    adminOnly: true, // Только для администраторов
  },
  {
    name: 'Настройки',
    href: '/settings',
    icon: Cog6ToothIcon,
    iconSolid: Cog6ToothSolidIcon,
    description: 'Конфигурация системы',
  },
];

const Sidebar: React.FC<SidebarProps> = ({ mobile = false, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const unreadNotificationsCount = useUnreadNotificationsCount();

  // Получаем количество заказов в работе
  const { data: salesNotifications } = useQuery({
    queryKey: ['sales-notifications'],
    queryFn: () => apiClient.getNotifications(),
    refetchInterval: 5000, // Обновляем каждые 5 секунд
    refetchOnWindowFocus: true, // Обновляем при фокусе окна
    staleTime: 0, // Данные всегда считаем устаревшими
  });

  const ordersInProgress = salesNotifications?.count || 0;


  const isActiveRoute = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  // Фильтруем меню в зависимости от роли пользователя
  const filteredNavigation = navigation.filter((item) => {
    // Если элемент только для администраторов, проверяем роль
    if (item.adminOnly) {
      return user?.role === 'admin';
    }
    return true;
  });

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
      {/* Заголовок с логотипом */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <span className="text-2xl">🪱</span>
          <span className="ml-2 text-xl font-bold text-gray-900 dark:text-gray-100">
            WormTracker
          </span>
        </div>
        
        {mobile && onClose && (
          <button
            type="button"
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={onClose}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Навигация */}
      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
        {filteredNavigation.map((item) => {
          const isActive = isActiveRoute(item.href);
          const Icon = isActive ? item.iconSolid : item.icon;
          
          return (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={mobile ? onClose : undefined}
              className={({ isActive }) =>
                clsx(
                  'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150',
                  isActive
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                )
              }
            >
              <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
              <span className="flex-1">{item.name}</span>
              
              {/* Badge для уведомлений и заказов в работе */}
              {((item.href === '/notifications' && unreadNotificationsCount > 0) ||
                (item.href === '/sales' && ordersInProgress > 0) ||
                (item.badge && item.badge > 0)) && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                  {item.href === '/notifications'
                    ? (unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount)
                    : item.href === '/sales'
                    ? (ordersInProgress > 99 ? '99+' : ordersInProgress)
                    : (item.badge! > 99 ? '99+' : item.badge)
                  }
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Нижняя информация */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
          <div className="flex items-center">
            <div className="h-2 w-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
            <span className="text-sm text-green-800 dark:text-green-300 font-medium">
              Система работает
            </span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            Последняя синхронизация: только что
          </p>
        </div>
        
        <div className="mt-3 flex items-center justify-between px-2">
          <SystemClock />
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              v1.0.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
